import React, { useLayoutEffect, useRef, useState } from "react";
import { BADGE_SHAPES, type BadgeShape } from "./badgeShapes";
import { contrastText } from "./contrastText";
import { computeFitFontSize, LINE_HEIGHT } from "./fitText";
import "./unitBadge.css";

import angle from "./shapes/angle.svg?raw";
import burst from "./shapes/burst.svg?raw";
import burst2 from "./shapes/burst-2.svg?raw";
import calendar from "./shapes/calendar.svg?raw";
import circle from "./shapes/circle.svg?raw";
import diamond from "./shapes/diamond.svg?raw";
import freeLock from "./shapes/free-lock.svg?raw";
import halfPill from "./shapes/half-pill.svg?raw";
import rectangle from "./shapes/rectangle.svg?raw";
import star from "./shapes/star.svg?raw";
import tag from "./shapes/tag.svg?raw";
import threeQuarterCircle from "./shapes/three-quarter-circle.svg?raw";

const SHAPE_SVG: Record<BadgeShape, string> = {
    angle,
    burst,
    "burst-2": burst2,
    calendar,
    circle,
    diamond,
    "free-lock": freeLock,
    "half-pill": halfPill,
    rectangle,
    star,
    tag,
    "three-quarter-circle": threeQuarterCircle,
};

// Auto-fit tuning. Text is shrunk uniformly until it fits the shape's inner box.
const MIN_FONT_PX = 6;
const MAX_FONT_PX = 16;
const MAX_FONT_RATIO = 0.22; // starting/max text size as a fraction of badge size

/**
 * Per-shape text placement. Each shape's usable text area differs — a symmetric burst
 * centers fine, but e.g. the padlock's text belongs in the lower body, below the shackle.
 * width/height are the text box as a fraction of the badge; offsetX/Y shift its center
 * (fraction of the badge; +Y = down). Symmetric shapes use the default; tune an entry
 * as each shape is reviewed.
 */
interface TextArea {
    widthRatio: number;
    heightRatio: number;
    offsetXRatio: number;
    offsetYRatio: number;
    // Optional cap on wrapped lines (e.g. short banner shapes). The font shrinks until
    // the label wraps into at most this many lines. Omit for unlimited (fit to the box).
    maxLines?: number;
    // Optional rotation (degrees, + = clockwise) to align the text with a tilted shape.
    rotationDeg?: number;
    // Optional overall badge size multiplier (e.g. shrink a shape that reads too large).
    sizeScale?: number;
}
const DEFAULT_TEXT_AREA: TextArea = {
    widthRatio: 0.58,
    heightRatio: 0.52,
    offsetXRatio: 0,
    offsetYRatio: 0,
};
const SHAPE_TEXT_AREA: Partial<Record<BadgeShape, TextArea>> = {
    // Banner with a tapered bottom-right; nudge the text left and cap at two lines.
    angle: {
        widthRatio: 0.6,
        heightRatio: 0.5,
        offsetXRatio: -0.08,
        offsetYRatio: 0,
        maxLines: 2,
    },
    // Wall calendar: header/rings up top and a folded right edge, so drop the text into
    // the lower-left body. Up to 3 lines (shrinks to fit the smaller area).
    calendar: {
        widthRatio: 0.64,
        heightRatio: 0.45,
        offsetXRatio: -0.1,
        offsetYRatio: 0.15,
        maxLines: 3,
    },
    // Diamond/rhombus: the inscribed area is small (box corners poke past the edges when
    // width+height > 1), so use a tighter box (smaller text) and cap at two lines.
    diamond: {
        widthRatio: 0.42,
        heightRatio: 0.3,
        offsetXRatio: 0,
        offsetYRatio: 0,
        maxLines: 2,
    },
    // Padlock: sit the text in the lower body, clear of the shackle arch (~71% down).
    "free-lock": {
        widthRatio: 0.5,
        heightRatio: 0.4,
        offsetXRatio: 0,
        offsetYRatio: 0.21,
    },
    // Half-pill: default placement is good; just cap at two lines.
    "half-pill": {
        widthRatio: 0.58,
        heightRatio: 0.52,
        offsetXRatio: 0,
        offsetYRatio: 0,
        maxLines: 2,
    },
    // Rectangle: full rounded-rect, the largest usable area — open the box up so the
    // text can be much larger.
    rectangle: {
        widthRatio: 0.82,
        heightRatio: 0.74,
        offsetXRatio: 0,
        offsetYRatio: 0,
    },
    // Star: usable area is the inner pentagon, centered a bit above the badge center —
    // nudge the text up and shrink to sit within it.
    star: {
        widthRatio: 0.46,
        heightRatio: 0.44,
        offsetXRatio: 0,
        offsetYRatio: -0.06,
        maxLines: 2,
    },
    // Tag: hole/point on the left and the body tilts down-to-the-right (~7deg), so shift
    // the text right, rotate it to parallel the tag edges, and cap at two lines.
    tag: {
        widthRatio: 0.5,
        heightRatio: 0.42,
        offsetXRatio: 0.07,
        offsetYRatio: 0,
        rotationDeg: 4,
        maxLines: 2,
    },
    // Three-quarter circle reads as a large solid disc — render it smaller so it doesn't
    // dominate the row. Mass leans bottom-right (squared corner), so nudge text down+right.
    "three-quarter-circle": {
        widthRatio: 0.62,
        heightRatio: 0.56,
        offsetXRatio: 0,
        offsetYRatio: 0.02,
        sizeScale: 0.8,
    },
};

export interface UnitBadgeProps {
    shape: string;
    color: string;
    text: string;
    size?: number; // px
    className?: string;
    // Optional text-color override. Prefer the host's WCAG-correct foreground
    // (FeaturedUnit.colorForeground); falls back to the local contrastText.
    textColor?: string;
}

export const UnitBadge: React.FC<UnitBadgeProps> = ({
    shape,
    color,
    text,
    size,
    className,
    textColor,
}) => {
    const containerRef = useRef<HTMLSpanElement>(null);
    const textRef = useRef<HTMLSpanElement>(null);
    const area = SHAPE_TEXT_AREA[shape as BadgeShape] ?? DEFAULT_TEXT_AREA;
    // Some shapes read better at a smaller overall size (e.g. the big three-quarter disc).
    const badgeSize = (size ?? 64) * (area.sizeScale ?? 1);
    const maxFont = Math.min(
        MAX_FONT_PX,
        Math.max(MIN_FONT_PX, badgeSize * MAX_FONT_RATIO),
    );
    const [fontSize, setFontSize] = useState<number>(maxFont);

    // Shrink the whole label uniformly until it fits the shape's text area — no
    // truncation/ellipsis. Binary-searches the largest font that fits both axes.
    useLayoutEffect(() => {
        const container = containerRef.current;
        const textEl = textRef.current;
        if (!container || !textEl) return;
        const boxW = container.clientWidth * area.widthRatio;
        const boxH = container.clientHeight * area.heightRatio;
        if (boxW <= 0 || boxH <= 0) return; // not laid out yet (e.g. jsdom)

        const best = computeFitFontSize({
            minFont: MIN_FONT_PX,
            maxFont,
            boxW,
            boxH,
            maxLines: area.maxLines,
            measure: (fontSize) => {
                textEl.style.fontSize = `${fontSize}px`;
                return {
                    width: textEl.scrollWidth,
                    height: textEl.scrollHeight,
                    lines: Math.round(textEl.scrollHeight / (fontSize * LINE_HEIGHT)),
                };
            },
        });
        setFontSize(best);
    }, [
        text,
        badgeSize,
        shape,
        maxFont,
        area.widthRatio,
        area.heightRatio,
        area.maxLines,
    ]);

    if (!BADGE_SHAPES.includes(shape as BadgeShape)) return null;
    const svg = SHAPE_SVG[shape as BadgeShape];
    const style = {
        ["--badge-fill" as string]: color,
        ["--badge-text" as string]: textColor || contrastText(color),
        ["--badge-size" as string]: `${badgeSize}px`,
    } as React.CSSProperties;
    const textTransform =
        [
            area.offsetXRatio || area.offsetYRatio
                ? `translate(${area.offsetXRatio * badgeSize}px, ${area.offsetYRatio * badgeSize}px)`
                : "",
            area.rotationDeg ? `rotate(${area.rotationDeg}deg)` : "",
        ]
            .filter(Boolean)
            .join(" ") || undefined;
    return (
        <span
            ref={containerRef}
            className={`unit-badge${className ? ` ${className}` : ""}`}
            style={style}
        >
            <span
                className="unit-badge__shape"
                aria-hidden="true"
                dangerouslySetInnerHTML={{ __html: svg }}
            />
            <span
                ref={textRef}
                className="unit-badge__text"
                style={{
                    fontSize: `${fontSize}px`,
                    maxWidth: `${area.widthRatio * 100}%`,
                    maxHeight: `${area.heightRatio * 100}%`,
                    transform: textTransform,
                }}
            >
                {text}
            </span>
        </span>
    );
};
